/**
 * client — typed HTTP/SSE client to lunaria-server
 *
 * 取代桌面 IDE 的 acquireVsCodeApi+postMessage transport（spec §6.6 的 shim）。
 * 类型直接 type-import 自 lunaria-server，单一真相源、零运行时耦合（esbuild 会
 * 剥掉 type-only import）。SSE 走 fetch 流式读取 agent 事件。
 */

import type { ProjectMeta, SceneSummary, AssetSummary, CompileResult, AgentEvent, StyleMeta, WritingAssistBody, WritingAssistResult } from "lunaria-server";
import { canonicalLooseAssetId } from "@lunaverse-ide/shared";
import type { AssetMapping, AssetKind } from "../assets/mapping.js";

export type { ProjectMeta, SceneSummary, AssetSummary, CompileResult, AgentEvent, StyleMeta, WritingAssistBody, WritingAssistResult };

/** Which optional, environment-gated server capabilities are available. */
export interface Capabilities {
  /** AI image generation (POST /assets/generate) is wired. */
  readonly imageGen: boolean;
  /** Automatic character matting via the IDE-compatible process-cutout service. */
  readonly cutout: boolean;
  /** The native `lsc` compiler binary is available. */
  readonly compiler: boolean;
}

/** Body for POST /assets/generate (web side never deals in file paths). */
export interface GenerateAssetBody {
  readonly prompt: string;
  /**
   * The asset family this generation targets. Only the visual kinds
   * (background / character / cg) can actually be generated — audio (music / sfx)
   * is upload-only — but the field accepts the full `AssetKind` so the container's
   * generic `onGenerate` forwards cleanly; the server ignores non-visual kinds.
   */
  readonly kind?: AssetKind;
  /** The scene symbol this asset is destined for (optional hint). */
  readonly symbol?: string;
  /** Character identity for IDE ep-sprite template variables. */
  readonly charId?: string;
  /** Selected art style (风格) family; the server renders the final prompt. */
  readonly family?: string;
  /** Optional model override; server picks a default otherwise. */
  readonly model?: string;
  /** Existing assets to condition the generation on (image-to-image). */
  readonly referenceAssetIds?: string[];
}

/** Result of a successful POST /assets/generate. */
export interface GenerateAssetResult {
  readonly assetId: string;
  readonly bytes: number;
}

export interface CutoutAssetResult extends GenerateAssetResult {
  readonly sourceAssetId: string;
}

export interface RenderedPromptPreview {
  readonly finalPrompt: string;
  readonly model: string;
  readonly aspectRatio: string;
  readonly referenceImageUrls: string[];
  readonly family: string;
}

export interface DownloadProjectResult {
  readonly blob: Blob;
  readonly filename: string;
}

export interface AccountUsageBreakdown {
  readonly [category: string]: number;
}

export interface AccountCostUsage {
  readonly period: string;
  readonly periodStart?: string;
  readonly periodEnd?: string;
  readonly currency: "USD";
  readonly usedCostUsd: number;
  readonly quotaCostUsd: number | null;
  readonly remainingCostUsd?: number;
  readonly unlimited?: boolean;
  readonly breakdown?: AccountUsageBreakdown;
}

export interface AccountTokenUsage {
  readonly period: string;
  readonly periodStart?: string;
  readonly periodEnd?: string;
  readonly used: number;
  readonly quota: number | null;
  readonly remaining?: number;
  readonly unlimited?: boolean;
}

export type AccountUsage = AccountCostUsage | AccountTokenUsage;

/** Current editor/canvas state sent with free-form agent requests. */
export interface AgentRunContext {
  readonly activeEpisodeId?: string;
  readonly activeView?: string;
  readonly sourceExcerpt?: string;
  readonly selectedLine?: number;
  readonly selectedNodeSummary?: string;
  readonly selectedSourceWindow?: string;
  readonly diagnostics?: readonly string[];
}

export type AgentRunMode = "create" | "adapt";

/** Body for POST /assets/suggest-prompt (AI drafts an image prompt for review). */
export interface SuggestPromptBody {
  /** Visual kind being depicted. */
  readonly kind?: AssetKind;
  /** The semantic/symbol name of the asset (may be non-English — the AI translates intent). */
  readonly name?: string;
  /** Selected art-style family name, woven into the prompt. */
  readonly styleName?: string;
  /** The style's render-recipe description (concrete style guidance — prevents style drift). */
  readonly styleDescription?: string;
  /** Optional free-form direction from the user (e.g. current prompt draft, mood). */
  readonly notes?: string;
}

export interface LunariaClientOptions {
  /** API base，浏览器同源时留空 */
  readonly baseUrl?: string;
  readonly token: string;
  readonly fetchImpl?: typeof fetch;
}

export class LunariaApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "LunariaApiError";
  }
}

function filenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Fall through to the ASCII filename.
    }
  }
  const ascii = /filename="([^"]+)"/i.exec(header)?.[1] ?? /filename=([^;]+)/i.exec(header)?.[1];
  return ascii?.trim();
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function normalizeAccountUsageResponse(body: unknown): AccountUsage | undefined {
  const usage =
    body && typeof body === "object" && "usage" in body
      ? (body as { usage?: unknown }).usage
      : body;
  if (!usage || typeof usage !== "object") return undefined;
  const rec = usage as Record<string, unknown>;
  if (typeof rec.period !== "string") return undefined;

  if (rec.currency === "USD") {
    if (!isFiniteNumber(rec.usedCostUsd) || !(isFiniteNumber(rec.quotaCostUsd) || rec.quotaCostUsd === null)) {
      return undefined;
    }
    const breakdown =
      rec.breakdown && typeof rec.breakdown === "object"
        ? Object.fromEntries(
            Object.entries(rec.breakdown as Record<string, unknown>).filter((entry): entry is [string, number] =>
              isFiniteNumber(entry[1]),
            ),
          )
        : undefined;
    return {
      period: rec.period,
      ...(typeof rec.periodStart === "string" ? { periodStart: rec.periodStart } : {}),
      ...(typeof rec.periodEnd === "string" ? { periodEnd: rec.periodEnd } : {}),
      currency: "USD",
      usedCostUsd: rec.usedCostUsd,
      quotaCostUsd: rec.quotaCostUsd,
      ...(isFiniteNumber(rec.remainingCostUsd) ? { remainingCostUsd: rec.remainingCostUsd } : {}),
      ...(rec.unlimited === true ? { unlimited: true } : {}),
      ...(breakdown && Object.keys(breakdown).length > 0 ? { breakdown } : {}),
    };
  }

  if (!isFiniteNumber(rec.used) || !(isFiniteNumber(rec.quota) || rec.quota === null)) return undefined;
  return {
    period: rec.period,
    ...(typeof rec.periodStart === "string" ? { periodStart: rec.periodStart } : {}),
    ...(typeof rec.periodEnd === "string" ? { periodEnd: rec.periodEnd } : {}),
    used: rec.used,
    quota: rec.quota,
    ...(isFiniteNumber(rec.remaining) ? { remaining: rec.remaining } : {}),
    ...(rec.unlimited === true ? { unlimited: true } : {}),
  };
}

export class LunariaClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly opts: LunariaClientOptions) {
    this.baseUrl = opts.baseUrl ?? "";
    // Bind the global fetch to globalThis: calling it as `this.fetchImpl(...)`
    // would otherwise invoke native fetch with `this` = this client instance,
    // which the browser rejects ("Illegal invocation").
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  async writingAssist(projectId: string, body: WritingAssistBody, signal?: AbortSignal): Promise<WritingAssistResult> {
    const response = await this.request("POST", `/api/projects/${encodeURIComponent(projectId)}/writing-assist`, body, signal);
    return await response.json() as WritingAssistResult;
  }

  private authHeaders(json: boolean): Record<string, string> {
    const headers: Record<string, string> = { authorization: `Bearer ${this.opts.token}` };
    if (json) headers["content-type"] = "application/json";
    return headers;
  }

  private async request(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const init: RequestInit = { method, headers: this.authHeaders(body !== undefined), ...(signal ? { signal } : {}) };
    if (body !== undefined) init.body = JSON.stringify(body);
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, init);
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      const message = (detail as { error?: string }).error ?? `HTTP ${response.status}`;
      throw new LunariaApiError(response.status, message);
    }
    return response;
  }

  /**
   * Log in against the gateway via the server proxy (public — no bearer yet).
   * Returns the token to store + send as the bearer on subsequent calls.
   */
  async login(username: string, password: string): Promise<string> {
    const response = await this.fetchImpl(`${this.baseUrl}/api/auth/login`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new LunariaApiError(response.status, (detail as { error?: string }).error ?? `HTTP ${response.status}`);
    }
    return (await response.json()).token as string;
  }

  /**
   * Invite-code sign-up via the server proxy (public — no bearer yet). Mirrors
   * login: the gateway runs a closed beta gated on a mandatory invite code and
   * returns a token immediately (auto-login), so we return it to store + use as
   * the bearer, exactly like login.
   */
  async register(username: string, password: string, inviteCode: string): Promise<string> {
    const response = await this.fetchImpl(`${this.baseUrl}/api/auth/register`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password, inviteCode }),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new LunariaApiError(response.status, (detail as { error?: string }).error ?? `HTTP ${response.status}`);
    }
    return (await response.json()).token as string;
  }

  /** Fetch the logged-in user (authed proxy to the gateway). */
  async me(): Promise<{ user?: { name?: string; username?: string } }> {
    const res = await this.request("GET", "/api/auth/me");
    return (await res.json()) as { user?: { name?: string; username?: string } };
  }

  /** Fetch the current billing/usage balance from the gateway usage ledger. */
  async accountUsage(): Promise<AccountUsage> {
    const res = await this.request("GET", "/api/auth/usage");
    const usage = normalizeAccountUsageResponse(await res.json());
    if (!usage) throw new LunariaApiError(502, "usage response missing");
    return usage;
  }

  async listProjects(): Promise<ProjectMeta[]> {
    const res = await this.request("GET", "/api/projects");
    return (await res.json()).projects as ProjectMeta[];
  }

  async createProject(name: string): Promise<ProjectMeta> {
    const res = await this.request("POST", "/api/projects", { name });
    return (await res.json()).project as ProjectMeta;
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.request("DELETE", `/api/projects/${encodeURIComponent(projectId)}`);
  }

  /** Project-plan protagonist; drives the same left/right rule as the IDE. */
  async getProtagonist(projectId: string): Promise<string | null> {
    const res = await this.request("GET", `/api/projects/${encodeURIComponent(projectId)}/protagonist`);
    const value = (await res.json() as { protagonist?: unknown }).protagonist;
    return typeof value === "string" && value.length > 0 ? value : null;
  }

  async downloadProject(projectId: string): Promise<DownloadProjectResult> {
    const res = await this.request("GET", `/api/projects/${encodeURIComponent(projectId)}/download`);
    return {
      blob: await res.blob(),
      filename: filenameFromContentDisposition(res.headers.get("content-disposition")) ?? `${projectId}.zip`,
    };
  }

  async listScenes(projectId: string): Promise<SceneSummary[]> {
    const res = await this.request("GET", `/api/projects/${encodeURIComponent(projectId)}/scenes`);
    return (await res.json()).scenes as SceneSummary[];
  }

  async listAssets(projectId: string): Promise<AssetSummary[]> {
    const res = await this.request("GET", `/api/projects/${encodeURIComponent(projectId)}/assets`);
    return (await res.json()).assets as AssetSummary[];
  }

  /** Upload an asset's raw bytes (reuse/upload — never AI-generated in v1). */
  async putAsset(projectId: string, assetId: string, data: Uint8Array, contentType?: string): Promise<string> {
    const canonicalId = canonicalLooseAssetId(assetId, contentType);
    const headers: Record<string, string> = { authorization: `Bearer ${this.opts.token}` };
    if (contentType) headers["content-type"] = contentType;
    const response = await this.fetchImpl(
      `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(canonicalId)}`,
      // Uint8Array is a valid fetch body at runtime; the DOM lib's BodyInit type
      // is narrower than reality, so assert through it.
      { method: "PUT", headers, body: data as unknown as BodyInit },
    );
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new LunariaApiError(response.status, (detail as { error?: string }).error ?? `HTTP ${response.status}`);
    }
    if (response.status === 204) return canonicalId;
    return ((await response.json()) as { assetId?: string }).assetId ?? canonicalId;
  }

  async deleteAsset(projectId: string, assetId: string): Promise<void> {
    await this.request(
      "DELETE",
      `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(assetId)}`,
    );
  }

  /**
   * Fetch an asset's bytes (authed) and return an object URL for an <img>.
   * The asset endpoint requires the bearer header, so a plain <img src> can't
   * load it directly — the caller is responsible for URL.revokeObjectURL.
   */
  async assetObjectUrl(projectId: string, assetId: string): Promise<string> {
    const res = await this.request(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(assetId)}`,
    );
    return URL.createObjectURL(await res.blob());
  }

  async readScene(projectId: string, sceneId: string): Promise<string> {
    const res = await this.request(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/scenes/${encodeURIComponent(sceneId)}`,
    );
    return (await res.json()).content as string;
  }

  async writeScene(projectId: string, sceneId: string, content: string): Promise<void> {
    await this.request(
      "PUT",
      `/api/projects/${encodeURIComponent(projectId)}/scenes/${encodeURIComponent(sceneId)}`,
      { content },
    );
  }

  async deleteScene(projectId: string, sceneId: string): Promise<void> {
    await this.request(
      "DELETE",
      `/api/projects/${encodeURIComponent(projectId)}/scenes/${encodeURIComponent(sceneId)}`,
    );
  }

  async compileScene(projectId: string, sceneId: string, mapping?: unknown): Promise<CompileResult> {
    const res = await this.request(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/scenes/${encodeURIComponent(sceneId)}/compile`,
      mapping === undefined ? {} : { mapping },
    );
    return (await res.json()) as CompileResult;
  }

  /** Report which optional, environment-gated server capabilities are wired. */
  async getCapabilities(): Promise<Capabilities> {
    const res = await this.request("GET", "/api/capabilities");
    return (await res.json()) as Capabilities;
  }

  /** The art-style catalog (风格选择) for the generate dialog. */
  async getStyles(): Promise<StyleMeta[]> {
    const res = await this.request("GET", "/api/styles");
    return ((await res.json()).styles ?? []) as StyleMeta[];
  }

  /** Preview the exact server-rendered prompt that generation will submit. */
  async previewAssetPrompt(body: { kind: "background" | "character" | "cg"; family?: string; prompt: string; symbol?: string; charId?: string; hasAssetReference?: boolean }): Promise<RenderedPromptPreview> {
    const res = await this.request("POST", "/api/styles/render-prompt", body);
    return (await res.json()) as RenderedPromptPreview;
  }

  /** Load an episode's symbol → assetId mapping (empty arrays when none stored). */
  async getMapping(projectId: string, episodeId: string): Promise<AssetMapping> {
    const res = await this.request(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/episodes/${encodeURIComponent(episodeId)}/mapping`,
    );
    return (await res.json()).mapping as AssetMapping;
  }

  /** Persist an episode's symbol → assetId mapping. */
  async putMapping(projectId: string, episodeId: string, mapping: AssetMapping): Promise<void> {
    await this.request(
      "PUT",
      `/api/projects/${encodeURIComponent(projectId)}/episodes/${encodeURIComponent(episodeId)}/mapping`,
      { mapping },
    );
  }

  /**
   * Generate a new asset via the server's image model. Returns the new
   * `assetId` + byte size on success. Throws a `LunariaApiError` (status 501)
   * when image generation is unavailable — gate the UI on `getCapabilities`.
   */
  async generateAsset(projectId: string, body: GenerateAssetBody): Promise<GenerateAssetResult> {
    const res = await this.request(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/assets/generate`,
      body,
    );
    return (await res.json()) as GenerateAssetResult;
  }

  /** Create a transparent WebP from an existing character image. */
  async cutoutAsset(projectId: string, assetId: string, target?: { symbol: string; charId: string }): Promise<CutoutAssetResult> {
    const res = await this.request(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(assetId)}/cutout`,
      target ?? {},
    );
    return (await res.json()) as CutoutAssetResult;
  }

  /**
   * Ask the AI to draft an image-generation prompt (POST /assets/suggest-prompt).
   * A single cheap LLM turn — it only WRITES a prompt string; the user reviews /
   * edits it and triggers generation separately. Throws on HTTP error (401/404/501).
   */
  async suggestAssetPrompt(projectId: string, body: SuggestPromptBody): Promise<string> {
    const res = await this.request(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/assets/suggest-prompt`,
      body,
    );
    const json = (await res.json()) as { prompt?: string };
    return typeof json.prompt === "string" ? json.prompt : "";
  }

  /** POST a JSON body and stream the SSE AgentEvents back to `onEvent`. */
  private async streamPost(path: string, body: unknown, onEvent: (event: AgentEvent) => void): Promise<void> {
    const response = await this.request("POST", path, body);
    if (!response.body) return;
    for await (const event of readSseEvents<AgentEvent>(response.body)) {
      onEvent(event);
    }
  }

  /**
   * Run the script-scoped agent, streaming AgentEvents. Resolves when the
   * stream ends (after a done/error event). Throws on a non-stream HTTP error
   * (e.g. 400/401/404/501) before the stream starts.
   */
  async runAgent(
    projectId: string,
    instruction: string,
    onEvent: (event: AgentEvent) => void,
    context?: AgentRunContext,
    mode?: AgentRunMode,
  ): Promise<void> {
    const body = {
      instruction,
      ...(context ? { context } : {}),
      ...(mode ? { mode } : {}),
    };
    await this.streamPost(`/api/projects/${encodeURIComponent(projectId)}/agent`, body, onEvent);
  }

  /**
   * Adapt an uploaded novel into an episode. The server splits the raw text
   * into chapters and streams the agent as it writes + compiles one chapter
   * (default the first). Same SSE contract as `runAgent`.
   */
  async runAdapt(
    projectId: string,
    novelText: string,
    onEvent: (event: AgentEvent) => void,
    chapterIndex?: number,
    confirmationInstruction?: string,
  ): Promise<void> {
    const body = {
      novelText,
      ...(chapterIndex === undefined ? {} : { chapterIndex }),
      ...(confirmationInstruction ? { confirmationInstruction } : {}),
    };
    await this.streamPost(`/api/projects/${encodeURIComponent(projectId)}/adapt`, body, onEvent);
  }
}

/** Parse an SSE byte stream into `data:` JSON payloads. */
export async function* readSseEvents<T>(body: ReadableStream<Uint8Array>): AsyncGenerator<T> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const line = block.split("\n").find((l) => l.startsWith("data: "));
        if (line) yield JSON.parse(line.slice("data: ".length)) as T;
      }
    }
  } finally {
    reader.releaseLock();
  }
}
